import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchFilterDimensionsData,
  fetchFilterDimensionsByCountry,
  fetchMarketOverviewMetricFilterDimensions,
} from "../api/marketOverviewApi";

// Async thunk for fetching available platforms for Market Overview
export const fetchMarketOverviewPlatforms = createAsyncThunk(
  "marketOverviewFilters/fetchPlatforms",
  /** @param {{ country: string | string[]; id_client: any }} arg */
  async ({ country, id_client }, { rejectWithValue }) => {
    try {
      let res;
      if (!Array.isArray(country)) {
        let countryToFetch = country;
        res = await fetchFilterDimensionsData({
          country: countryToFetch,
          id_client,
        });
      } else {
        res = await fetchFilterDimensionsByCountry({
          countries: country,
          id_client,
        });
      }
      return res;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch market overview platforms"
      );
    }
  }
);

// Async thunk for fetching dynamic franchises for Market Overview
export const fetchMarketOverviewFranchises = createAsyncThunk(
  "marketOverviewFilters/fetchFranchises",
  /** @param {{ countries: string[]; id_client: any; refresh_from: number; refresh_to: number; platform: string; segment_type: string; is_part_of_chain: boolean; franchise_name?: any }} arg */
  async (
    {
      countries,
      id_client,
      refresh_from,
      refresh_to,
      platform,
      segment_type,
      is_part_of_chain,
      franchise_name,
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await fetchMarketOverviewMetricFilterDimensions({
        countries,
        id_client,
        refresh_from,
        refresh_to,
        platform,
        segment_type,
        is_part_of_chain,
        franchise_name,
        franchise_only: true,
      });
      return res;
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to fetch market overview franchises"
      );
    }
  }
);

const initialState = {
  availablePlatforms: [],
  availableFranchises: [],
  imageAuditFeatureEnabled: true,
  platformsLoading: false,
  franchisesLoading: false,
  error: null,
};

const marketOverviewFiltersSlice = createSlice({
  name: "marketOverviewFilters",
  initialState,
  reducers: {
    clearAvailablePlatforms: (state) => {
      state.availablePlatforms = [];
    },
    clearAvailableFranchises: (state) => {
      state.availableFranchises = [];
    },
    setAvailableFranchises: (state, action) => {
      state.availableFranchises = Array.isArray(action.payload)
        ? action.payload
        : [];
      state.franchisesLoading = false;
    },
    setMarketOverviewDynamicDimensions: (state, action) => {
      const payload = action.payload || {};
      state.availableFranchises = Array.isArray(payload.franchise_names)
        ? payload.franchise_names
        : [];
      state.imageAuditFeatureEnabled =
        payload.image_audit_feature_enabled !== false;
      state.franchisesLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMarketOverviewPlatforms.pending, (state) => {
        state.platformsLoading = true;
        state.error = null;
      })
      .addCase(fetchMarketOverviewPlatforms.fulfilled, (state, action) => {
        state.platformsLoading = false;
        state.error = null;

        // Extract platforms from API response
        if (action.payload.platforms && Array.isArray(action.payload.platforms)) {
          state.availablePlatforms = action.payload.platforms;
        } else {
          state.availablePlatforms = [];
        }
      })
      .addCase(fetchMarketOverviewPlatforms.rejected, (state, action) => {
        state.platformsLoading = false;
        state.error = action.payload;
        console.error(
          "[marketOverviewFiltersSlice] Failed to fetch platforms:",
          action.payload
        );
      })
      .addCase(fetchMarketOverviewFranchises.pending, (state) => {
        state.franchisesLoading = true;
        state.error = null;
      })
      .addCase(fetchMarketOverviewFranchises.fulfilled, (state, action) => {
        state.franchisesLoading = false;
        state.error = null;
        const payload = action.payload || {};
        state.availableFranchises = Array.isArray(payload.franchise_names)
          ? payload.franchise_names
          : [];
        state.imageAuditFeatureEnabled =
          payload.image_audit_feature_enabled !== false;
      })
      .addCase(fetchMarketOverviewFranchises.rejected, (state, action) => {
        state.franchisesLoading = false;
        state.error = action.payload;
        console.error(
          "[marketOverviewFiltersSlice] Failed to fetch franchises:",
          action.payload
        );
      });
  },
});

export const {
  clearAvailablePlatforms,
  clearAvailableFranchises,
  setAvailableFranchises,
  setMarketOverviewDynamicDimensions,
} =
  marketOverviewFiltersSlice.actions;

// Selectors
export const selectAvailablePlatforms = (state) =>
  state.marketOverviewFilters.availablePlatforms;
export const selectPlatformsLoading = (state) =>
  state.marketOverviewFilters.platformsLoading;
export const selectAvailableFranchises = (state) =>
  state.marketOverviewFilters.availableFranchises;
export const selectFranchisesLoading = (state) =>
  state.marketOverviewFilters.franchisesLoading;
export const selectPlatformsError = (state) =>
  state.marketOverviewFilters.error;
export const selectImageAuditFeatureEnabled = (state) =>
  state.marketOverviewFilters.imageAuditFeatureEnabled;

export default marketOverviewFiltersSlice.reducer;

